import { makeAutoObservable } from "mobx";
import { errorNotification } from "../../layout/components/Notifications/notifications";
import { uploadLLMDocument, getFileLLMStatus, getAll, getLLMResult, deleteDocument, getCurrencyRate, addItems, confirmLLM, updateFilesStatus, submitFiles } from "./magic-box.service";
import { CURRENCY_SYMBOL } from "../../../common/constants/currency-symbol.const";
import { handleUrlFileDownload } from "../../../common/utils/network.utils";

export const MAX_MAGIC_BOX_FILE_SIZE_MB = 100;
export const LLM_FAILED_MESSAGE = 'Despite its best efforts, Magic Box AI was unable to describe this document.';
export const LLM_IN_PROCESS_MESSAGE = 'Hang tight! Our AI is reviewing your document to verify its details and ensure it meets our processing criteria. This step will only take a few seconds';
export const AI_LOADING_MSG_WELCOME = `Hold on! Our AI is now retrieving the detailed assets and liabilities from your document. This step might take up to 2 minutes, but you can continue to the next step if you're in a hurry. We'll notify you once everything is ready!`;
export const AI_LOADING_ACCOUNT = `Hold on! Our AI is now retrieving the detailed assets and liabilities from your document. This step might take up to 2 minutes. Feel free to explore Vyzer while we work—we'll notify you as soon as everything is ready!`;
export const SUPPORTED_FILE_FORMATS = ['.pdf', '.csv', '.xls', '.xlsx', '.docx', '.jpg', '.jpeg', '.png', 'gif', 'webp', 'bmp', '.xlsm'];
export class MagicBoxAiStore {

  metadataStore = null;
  isDoneWithMagicBoxAI = false;
  files = null;
  filesForUpload = null;
  isUploading = false;
  loading = false;
  llmResult = null;
  isTyping = false;
  switchToManualReview = false;
  currencyTable = { 'USD': 1 };
  showMissingFieldsError = 0;
  backupData = null;
  doneModal = null;
  filesForDoneModal = null;
  timeoutId = null;
  isDownloading = false;
  isDeleting = false;
  isDontHaveBalanceSheetClicked = false;
  deletingFiles = new Set();

  constructor(metadataStore) {
    this.metadataStore = metadataStore;
    makeAutoObservable(this);
  }

  async fetchData() {
    this.setLoading(true);
    try {
      const llmFiles = await getAll();
      if (llmFiles) {
        this.setFiles(llmFiles);
        this.checkFilesStatusPeriodically();
      } else {
        this.setFiles([]);
      }
    } catch (e) {
      console.log(e);
    } finally {
      this.setLoading(false);
    }
  }

  async checkFilesStatusPeriodically() {
    if (!this.notReadyPendingFiles?.length) {
      this.checkResultPeriodically();
      return;
    };

    this.timeoutId = setTimeout(() => this.fetchData(), 3000);
  }

  async checkResultPeriodically() {
    if (!this.notReadyFilesInReview?.length) {
      this.downloadLLMResult();
      return;
    };
    this.timeoutId = setTimeout(() => this.fetchData(), 3000);
  }

  async downloadLLMResult() {
    this.setIsDownloading(true);
    try {
      const llmResult = await getLLMResult();
      if (llmResult.statusCode) {
        errorNotification('Something went wrong');
      } else {
        llmResult.data = {
          items: this.fixItemsData(llmResult.data.items),
          tickers: this.fixItemsData(llmResult.data.tickers)
        };
        this.setLLMResult(llmResult);
        this.processLLMResultCurrencies([...llmResult?.data?.items, ...llmResult?.data?.tickers]);
      }
    } catch (e) {
      console.log(e);
      errorNotification('Something went wrong');
    } finally {
      this.setIsDownloading(false);
    }
  }

  async uploadDocument(acceptedFiles, fileRejections) {
    if (this.isUploading) return;

    const hasAcceptedFiles = (acceptedFiles && acceptedFiles.length > 0);
    const filesForUpload = (hasAcceptedFiles ? acceptedFiles : fileRejections);
    this.setFilesForUpload(filesForUpload);

    if (hasAcceptedFiles) {
      this.setUploading(true);
      try {
        let formData = new FormData();
        for (let i = 0; i < acceptedFiles.length; i++) {
          formData.append('files', acceptedFiles[i], acceptedFiles[i].name);
        }
        const uploadedDocuments = await uploadLLMDocument(formData);
        if (uploadedDocuments.statusCode) {
          errorNotification('Something went wrong');
        } else {
          await this.fetchData();
        }
      } catch (e) {
        errorNotification(e.message);
      } finally {
        this.setUploading(false);
      }
    }
  }

  async isFileReady(fileId) {
    const llmStatus = await getFileLLMStatus(fileId);
    return ('in process' !== llmStatus);
  }

  async submit(toUpdate, createNewAssets, updateExistingAssets) {
    clearTimeout(this.timeoutId);
    await this.deleteRejectedFiles();
    if (!this.filesPending?.length) {
      return;
    }

    if (this.isAllPendingFilesRejected) {
      await this.fetchData();
      return;
    }

    if (this.isAllPendingFilesFailed) {
      await this.setPendingFilesStatus("Processing");
      this.setFilesForDoneModal(this.filesPending.map(file => file.originalName));
      this.setDoneModal('FAILED');
      await this.fetchData();
      return;
    }

    let notes = null;

    if (!this.isAllPendingFilesRejected && (createNewAssets || updateExistingAssets)) {
      const fileHandler = ((createNewAssets && updateExistingAssets) ? 'both' : (createNewAssets ? 'create' : 'update'));
      notes = fileHandler;
    }

    try {
      const fileIds = this.filesPending.map(file => file.id);

      if (toUpdate) {
        await confirmLLM(false, 'Pending', notes);
        this.setFilesForDoneModal(this.filesPending.map(file => file.originalName));
        this.setDoneModal('FAILED');
      } else {
        await submitFiles(fileIds, "Pre-Process", notes);
      }

    } catch (e) {
      console.error('Submit failed:', e);
      errorNotification('Something went wrong');
    }

    await this.fetchData();
  }

  async onFailedFilesForReview() {
    clearTimeout(this.timeoutId);
    await this.setInReviewFilesStatus("Processing");
    this.setFilesForDoneModal(this.filesInReview.map(file => file.originalName));
    this.setDoneModal('FAILED');
    await this.fetchData();
  }

  fixItemsData(llmResultData) {
    if (!Array.isArray(llmResultData)) {
      return;
    }

    let symbolsTable;
    for (let i = 0, l = llmResultData.length; i < l; i++) {
      const { amount, currency } = llmResultData[i];
      if (amount < 0) {
        llmResultData[i].amount = Math.abs(amount);
      }

      if (currency) {
        if (!CURRENCY_SYMBOL[currency]) {
          if (!symbolsTable) {
            symbolsTable = Object.entries(CURRENCY_SYMBOL).map(([code, symbol]) => [symbol, code]);
          }

          llmResultData[i].currency = (symbolsTable[currency] ?? 'USD');
        }
      } else {
        llmResultData[i].currency = 'USD';
      }
    }

    return llmResultData;
  }

  async setPendingFilesStatus(status) {
    await this.updateFilesStatus(this.filesPending.map(file => file.id), status);
  }

  async setInReviewFilesStatus(status) {
    await this.updateFilesStatus(this.filesInReview.map(file => file.id), status);
  }

  async updateFilesStatus(ids, status) {
    try {
      await updateFilesStatus(ids, status);
    }
    catch (e) {
      console.log(e);
      errorNotification('Something went wrong');
    }
  }

  async deleteRejectedFiles() {
    if (!this.files) return;
    const rejectedFiles = this.files.filter(file => file.llmStatus === 'rejected');
    if (rejectedFiles.length === 0) return;
    try {
      this.setIsDeleting(true);
      await Promise.all(rejectedFiles.map(async file => await deleteDocument(file.id)));
      await this.fetchData();
    } catch (e) {
      console.log(e);
    } finally {
      this.setIsDeleting(false);
    }
  }

  async deletePreProcessFiles() {
    if (!this.filesInReview) return;
    if (this.filesInReview === 0) return;
    try {
      this.setIsDeleting(true);
      await Promise.all(this.filesInReview.map(async file => await deleteDocument(file.id)));
      await this.fetchData();
    } catch (e) {
      console.log(e);
    } finally {
      this.setIsDeleting(false);
    }
  }

  async deletePendingFiles() {
    if (!this.filesPending) return;
    try {
      this.setIsDeleting(true);
      await Promise.all(this.filesPending.map(async file => await deleteDocument(file.id)));
      await this.fetchData();
    } catch (e) {
      console.log(e);
    } finally {
      this.setIsDeleting(false);
    }
  }

  async deleteAllFiles() {
    if (!this.files) return;
    try {
      this.setIsDeleting(true);
      await Promise.all(this.files.map(async file => await deleteDocument(file.id)));
      await this.fetchData();
    } catch (e) {
      console.log(e);
    } finally {
      this.setIsDeleting(false);
    }
  }

  async deleteDocument(id) {
    try {
      if (!this.files) return;
      this.deletingFiles.add(id);
      const file = this.files.find(file => file.id === id);
      if (!file) return;
      await deleteDocument(id);
      await this.fetchData();
    } catch (e) {
      console.log(e);
    } finally {
      this.deletingFiles.delete(id);
    }
  }

  async getCurrencyRate(currency) {
    let currencyRate = 0;
    if (!currency) return 0;
    if (!this.currencyTable[currency]) {
      try {
        const currencyRateObj = await getCurrencyRate(currency);
        if (currencyRateObj.statusCode) { } else {
          currencyRate = currencyRateObj.rate;
        }
      } catch (_e) {

      }
      // this.currencyTable[currency] = currencyRate;
    }

    return currencyRate;
  }

  async addItems() {
    if (this.isAnyAccountWithoutCategory || this.isAnyAccountWithoutAllFields) {
      this.setShowMissingFieldsError();
      errorNotification(this.isAnyAccountWithoutCategory ? "Please select category for all items" : "Please fill all empty fields");
    } else {
      const items = {
        assets: []
      };

      if (this.processedLLMResult?.items?.length) {
        items.assets.push(...this.processedLLMResult.items.map(item => {
          return {
            categoryId: item.categoryId,
            value: +item.amount,
            currency: item.currency,
            title: item.item_name,
            fund_name: item.fund_name
          };
        }))
      }

      items.assets.push(...this.processedLLMResult?.tickers?.map(item => {
        return {
          categoryId: item.categoryId,
          value: +item.quantity,
          currency: item.currency,
          title: item.symbol,
          stocks: [{
            pricePerShare: +item.price_per_share,
            numberOfShares: +item.quantity,
            symbol: item.symbol
          }]
        };
      }))


      try {
        const response = await addItems(items);
        if (response?.statusCode) {
          errorNotification('Something went wrong');
        } else {
          await confirmLLM(true);
          await this.fetchData();
          this.setDoneModal('SUCCESS');
        }
      } catch (e) {
        errorNotification('Something went wrong');
      }
    }
  }

  async processLLMResultCurrencies(llmResultData) {
    if (!Array.isArray(llmResultData)) {
      return;
    }

    const uniqueMissingCurrencies = [
      ...new Set(llmResultData.map(item => item.currency).filter(cur => !this.currencyTable.hasOwnProperty(cur)))
    ];

    const currencyPromises = uniqueMissingCurrencies.map(currency => this.getCurrencyRate(currency));
    const currencyRateValues = await Promise.all(currencyPromises);
    const currencyRates = uniqueMissingCurrencies.reduce((acc, cur, index) => {
      acc[cur] = currencyRateValues[index] || 1;
      return acc;
    }, {});
    // console.log("Currency Rates", currencyRates);
    this.setCurrencyRates(currencyRates);

  }

  get processedLLMResult() {
    const categoriesTitleObj = this.metadataStore.categoriesTitleObjForVault;
    const categoriesObj = this.metadataStore.categoriesObj;
    const items = (this.llmResult?.data?.items || []).map(item => {
      const lowerCaseCategory = item.category?.toLowerCase();
      const currencyRate = this.currencyTable[item.currency];
      const categoryId = item.categoryId ? item.categoryId : categoriesTitleObj[lowerCaseCategory]?.id || null;
      const classId = categoryId ? categoriesObj[categoryId]?.classId || null : null;
      const relevantClass = classId ? this.metadataStore.classes.find(cls => cls.id === classId) : null;
      return {
        ...item,
        categoryId,
        classId,
        color: relevantClass?.color ?? '#FFFFFF',
        icon: relevantClass?.icon,
        usdValue: isNaN(item.amount) ? undefined : ((item.amount || 0) / (currencyRate || 1)),
        isFetchRate: !currencyRate
      };
    }).filter(item => item);

    const tickers = (this.llmResult?.data?.tickers || []).map(item => {
      const currencyRate = this.currencyTable[item.currency];
      const classId = 3;
      const categoryId = 39;
      const relevantClass = classId ? this.metadataStore.classes.find(cls => cls.id === classId) : null;


      return {
        ...item,
        classId,
        categoryId,
        color: relevantClass?.color ?? '#FFFFFF',
        icon: relevantClass?.icon,
        usdValue: isNaN(item.value) ? undefined : ((item.value || 0) / (currencyRate || 1)),
        isFetchRate: !currencyRate
      };
    }).filter(item => item);

    const result = {
      items,
      tickers
    };

    return result;
  }

  get totals() {
    const totalsObj = {
      assets: { count: 0, sum: 0 },
      liabilities: { count: 0, sum: 0 },
      tickers: { count: 0, sum: 0 }
    };

    if (!this.llmResult) return totalsObj;

    const assetsCategoriesObj = this.metadataStore.assetCategoriesObj;
    const liabilityCategoriesObj = this.metadataStore.liabilityCategoriesObj;
    const assets = this.processedLLMResult.items.filter(item => assetsCategoriesObj[item.categoryId]);
    const liabilities = this.processedLLMResult.items.filter(item => liabilityCategoriesObj[item.categoryId]);
    const tickers = this.processedLLMResult.tickers;

    totalsObj.assets.count = assets.length;
    totalsObj.assets.sum = assets.reduce((acc, item) => (acc + (isNaN(item.usdValue) ? 0 : item.usdValue)), 0);;

    totalsObj.liabilities.count = liabilities.length;
    totalsObj.liabilities.sum = liabilities.reduce((acc, item) => (acc + (isNaN(item.usdValue) ? 0 : item.usdValue)), 0);

    totalsObj.tickers.count = tickers.length;
    totalsObj.tickers.sum = tickers.reduce((acc, item) => (acc + (isNaN(item.usdValue) ? 0 : item.usdValue)), 0);

    return totalsObj;
  }

  get isFetchAnyRate() {
    return this.processedLLMResult?.items?.some(item => item.isFetchRate) || this.processedLLMResult?.tickers?.some(item => item.isFetchRate);
  }

  get isAnyAccountWithoutAllFields() {
    return this.processedLLMResult?.items?.some(item => !item.categoryId || isNaN(item.amount) || !item.currency || !item.item_name) ||
      !this.processedLLMResult?.tickers || this.processedLLMResult?.tickers?.some(item => !item.symbol || !item.currency || isNaN(item.quantity) || isNaN(item.price_per_share));
  }

  get isAnyAccountWithoutCategory() {
    return (!this.processedLLMResult?.items || this.processedLLMResult?.items?.some(item => !item.categoryId));
  }

  get firstAccountWithoutAllFields() {
    return this.processedLLMResult?.items?.findIndex(item => !item.categoryId || isNaN(item.amount) || !item.currency || !item.item_name);
  }

  get firstTickerWithoutAllFields() {
    return (this.processedLLMResult?.tickers?.findIndex(item => !item.symbol || !item.currency || isNaN(item.quantity) || isNaN(item.price_per_share)));
  }

  get isAllItemsSelectedForBulkEdit() {
    return (this.llmResult?.data?.items?.every(item => item.isItemForBulkEdit));
  }

  get isAllTickersSelectedForBulkEdit() {
    return (this.llmResult?.data?.tickers?.every(item => item.isItemForBulkEdit));
  }

  get isInBulkEditMode() {
    return (this.llmResult?.data?.items?.some(item => item.isItemForBulkEdit) || this.llmResult?.data?.tickers?.some(item => item.isItemForBulkEdit));
  }

  get isInTickersBulkEditMode() {
    return this.llmResult?.data?.tickers?.some(item => item.isItemForBulkEdit);
  }

  get bulkEditItemsCount() {
    return this.llmResult?.data?.items?.filter(item => item.isItemForBulkEdit).length || 0;
  }

  get bulkEditTickersCount() {
    return this.llmResult?.data?.tickers?.filter(item => item.isItemForBulkEdit).length || 0;
  }

  deleteBulkEditItems(step) {
    const count = this.bulkEditItemsCount;
    const backupText = `Successfully deleted ${count} item${(count > 1) ? 's' : ''}`;
    this.saveBackupData(backupText, step);

    const updatedData = this.llmResult.data[step]?.filter(item => !item.isItemForBulkEdit) ?? [];
    this.llmResult.data[step] = updatedData;
  }

  exitItemsBulkEditMode(step) {
    const updatedData = this.llmResult.data[step].map(item => {
      delete item.isItemForBulkEdit;
      return { ...item };
    });

    this.llmResult.data[step] = updatedData;
  }

  exitBulkEditMode() {
    this.llmResult.data.items.forEach(item => delete item.isItemForBulkEdit);
    this.llmResult.data.tickers.forEach(item => delete item.isItemForBulkEdit);
  }


  undoBulkEdit(step) {
    if (!this.backupData?.data) return;

    const updatedData = this.backupData.data[step].map(item => {
      delete item.isItemForBulkEdit;
      return { ...item };
    });

    this.llmResult.data[step] = updatedData;
    this.clearBackupData();
  }

  bulkEditItems(fieldsToChange, fieldName, step) {
    if (fieldsToChange.currency) {
      this.processLLMResultCurrencies([fieldsToChange]);
    }

    const count = this.bulkEditItemsCount;
    const backupText = `Successfully updated the ${fieldName} for ${count} item${(count > 1) ? 's' : ''}`;
    this.saveBackupData(backupText, step);

    const updatedData = this.llmResult.data[step].map(item => {
      if (!item.isItemForBulkEdit) return item;
      return { ...item, ...fieldsToChange, isItemForBulkEdit: false };
    });
    this.llmResult.data[step] = updatedData;
  }

  saveBackupData(text, step) {
    this.backupData = {
      data: {
        [step]: this.llmResult.data[step]?.slice() ?? []
      },
      text,
      timeoutId: setTimeout(() => this.clearBackupData(), 5000)
    };
  }

  clearBackupData() {
    if (this.backupData?.timeoutId) {
      clearTimeout(this.backupData.timeoutId);
    }

    this.backupData = null;
  }

  toggleAllItemsForBulkEdit(step) {
    if (!this.llmResult?.data) return;
    const newState = this.llmResult.data[step].some(item => !item.isItemForBulkEdit);

    const updatedData = this.llmResult.data[step].map(item => {
      return { ...item, isItemForBulkEdit: newState };
    });

    this.llmResult.data[step] = updatedData;
  }

  toggleItemForBulkEdit(index, step) {
    if (!this.llmResult?.data) return;

    const updatedData = [...this.llmResult.data[step]];
    const item = updatedData[index];
    item.isItemForBulkEdit = !item.isItemForBulkEdit;

    this.llmResult.data[step] = updatedData;
  }

  bulkEditFieldValue(field, step) {
    if (!this.llmResult?.data) return;

    const itemsForBulkEdit = this.processedLLMResult?.[step]?.filter(item => item.isItemForBulkEdit);
    return (itemsForBulkEdit.every(item => (item[field] === itemsForBulkEdit[0][field])) ? itemsForBulkEdit[0][field] : null);
  }

  setCurrencyRates(rates) {
    this.currencyTable = { ...this.currencyTable, ...rates };
  }

  setDoneModal(doneModal) {
    this.doneModal = doneModal;
  }

  setLLMResult(llmResult) {
    // console.log("Set results" , llmResult)
    this.llmResult = llmResult;
  }

  setFiles(files) {
    this.files = files;
  }

  setIsDeleting(isDeleting) {
    this.isDeleting = isDeleting;
  }

  isFileDeleting(fileId) {
    return this.deletingFiles.has(fileId);
  }

  setFilesForUpload(files) {
    this.filesForUpload = files;
  }

  setUploading(isUploading) {
    this.isUploading = isUploading;
  }

  setLoading(loading) {
    this.loading = loading;
  }

  setIsDownloading(isDownloading) {
    this.isDownloading = isDownloading;
  }

  changeReviewedItemCategory(index, categoryId) {
    const updatedData = JSON.parse(JSON.stringify(this.llmResult.data));
    console.log("Updated data", JSON.parse(JSON.stringify(this.llmResult.data)));

    updatedData.items[index].categoryId = categoryId;
    this.llmResult.data = updatedData;
  }

  updateReviewedItem(index, reviewedItem, step) {
    const updatedData = JSON.parse(JSON.stringify(this.llmResult.data));
    updatedData[step][index] = reviewedItem;
    this.processLLMResultCurrencies([reviewedItem]);
    this.llmResult.data = updatedData;
    console.log("Updated data", JSON.parse(JSON.stringify(this.llmResult.data)));
  }

  updateReviewedTicker(index, reviewedItem) {
    const updatedData = [...this.llmResult.data.tickers];
    updatedData[index] = reviewedItem;
    this.processLLMResultCurrencies([reviewedItem]);
    this.llmResult.data.tickers = updatedData;
  }

  deleteReviewedItem(index, step) {
    const updatedData = [...this.llmResult.data[step]];
    updatedData.splice(index, 1);

    this.llmResult.data[step] = updatedData;
  }

  setShowMissingFieldsError(show) {
    this.showMissingFieldsError++;
  }

  setIsTyping(isTyping) {
    this.isTyping = isTyping;
  }

  setFilesForDoneModal(files) {
    this.filesForDoneModal = files;
  }

  setIsDontHaveBalanceSheetClicked(isDontHaveBalanceSheetClicked) {
    this.isDontHaveBalanceSheetClicked = isDontHaveBalanceSheetClicked;
  }

  addNewItem(item, step) {
    this.processLLMResultCurrencies([item]);
    this.llmResult.data[step] = [...this.llmResult.data[step], item];
  }

  async download(id, fileName) {
    try {
      const baseApiUrl = process.env.REACT_APP_BASE_API_URL;
      await handleUrlFileDownload({ url: `${baseApiUrl}magic-box/download`, body: { id }, fileName })
    } catch (e) {
      console.log(e);
    }
  }

  get doneFiles() {
    return this.files?.filter(file => (file.status === 'Done'));
  }

  get filesInProcess() {
    return this.files?.filter(file => (file.status === 'Processing'));
  }

  get filesInReview() {
    return this.files?.filter(file => (file.status === 'Pre-Process'));
  }

  get filesPending() {
    return this.files?.filter(file => (file.status === 'Pending') && (file.entityHandler === 'LLM'));
  }

  get filesForCheck() {
    return this.files?.filter(file => ((file.status === 'Pending') && (file.llmStatus === 'in process')));
  }

  get rejectedFiles() {
    return this.files?.filter(file => (file.llmStatus === 'rejected'));
  }

  get llmStatusDoneFiles() {
    return this.files?.filter(file => (file.llmStatus === 'done'));
  }

  get llmStatusInFailedOrTimeoutFiles() {
    return this.files?.filter(file => (file.llmStatus === 'failed' || file.llmStatus === 'timeout'));
  }

  get isReadyForReview() {
    return (this.notReadyFilesInReview.length === 0);
  }

  get magicBoxStep() {
    if (this.isAllFilesforUploadFailed) {
      return 'failed';
    }

    if (this.filesPending?.length) {
      return 'upload';
    }

    if (this.filesInReview?.length) {
      return 'review';
    }

    return null;
  }

  get isAllFilesforUploadFailed() {
    return this.filesForUpload?.every(file => (file.errors?.length > 0));
  }

  get isAllPendingFilesFailed() {
    return this.filesPending?.every(file => ((!file.llmStatus) || (file.llmStatus === 'failed') || (file.llmStatus === 'timeout')));
  }

  get isAllPendingFilesRejected() {
    return this.filesPending?.every(file => (file.llmStatus === 'rejected'));
  }

  get areAllPendingFilesReady() {
    return this.filesPending?.every(file => (file.llmStatus !== 'in process')) ?? false;
  }

  get filesInReviewWithResults() {
    return this.filesInReview?.filter(file => (file.status === 'Pre-Process' && ((file.llmStatus === 'failed' || file.llmStatus === 'timeout') || (file.llmStatus === 'done' && file.llmResultURL))));
  }

  get isAllFilesInReviewFailed() {
    return this.filesInReview?.every(file => (file.llmStatus === 'failed' || file.llmStatus === 'timeout'));
  }

  get notReadyPendingFiles() {
    return this.filesPending?.filter(file => (file.llmStatus === 'in process'));
  }

  get notReadyFilesInReview() {
    return this.filesInReview?.filter(file => (file.llmStatus === 'in process') || (file.llmStatus === 'done' && !file.llmResultURL));
  }

  get hasResults() {
    return (this.hasItemsResults || this.hasTickersResults);
  }

  get hasItemsResults() {
    return (this.llmResult?.data?.items?.length > 0);
  }

  get hasTickersResults() {
    return (this.llmResult?.data?.tickers?.length > 0);
  }

  get doneReviewedFiles() {
    return this.filesInReview?.filter(file => (file.llmStatus === 'done')).length;
  }

  get handledFiles() {
    return this.files?.filter(file => (file.status === 'Done' || file.status === 'Processing'));
  }
}